import type * as models from 'pkg/api/models';
import type {
	MetaEntry,
	Group,
	OrganizationData,
	SplashScreenSettings,
} from 'pkg/api/models/group';
import { MetaField } from 'pkg/api/models/group';
import { isOrganization } from 'pkg/api/models/group/guards';
import * as json from 'pkg/json';

export function totalUserCount(group: Group) {
	return group.totalPlayerCount + group.totalAdminCount;
}

export function getOrganizationId(group: Group) {
	if (!group) {
		return null;
	}

	return isOrganization(group) ? group.id : group.organizationId;
}

export function getMetaEntry(
	group: Group,
	metaField: MetaField
): Nullable<MetaEntry> {
	return (
		group.meta?.find((entry: MetaEntry) => entry.key === metaField) ?? null
	);
}

export function getMetaValue(
	group: Group,
	metaField: MetaField
): Nullable<string> {
	const entry = getMetaEntry(group, metaField);

	if (entry) {
		return entry?.value || null;
	}

	return null;
}

/**
 *
 * @param group
 * @returns start and end timestamp of current season defaults to start of year to end of year
 */
export function getCurrentSeason(group: Group, now: Date = new Date()) {
	if (!group) {
		return { start: 0, end: 0 };
	}

	// Subtract one because seasonStartMonth is returned 1-12 and we need them to be 0-11 here
	let startMonth = group.seasonStartMonth - 1;

	if (!group.seasonStartMonth) {
		startMonth = 0;
	}

	let startYear = now.getUTCFullYear();
	if (now.getUTCMonth() < startMonth) {
		startYear -= 1;
	}

	const season = {
		start: Math.round(Date.UTC(startYear, startMonth, 1, 0, 0, 0, 0) / 1000),
		end: Math.round(Date.UTC(startYear + 1, startMonth, 1, 0, 0, 0, -1) / 1000),
	};

	return season;
}

/**
 *
 * @param group
 * @returns start and end timestamp of last season
 */
export function getLastSeason(group: Group, now: Date = new Date()) {
	const current = getCurrentSeason(group, now);

	const start = new Date(current.start * 1000);
	start.setUTCFullYear(start.getUTCFullYear() - 1);
	const end = new Date(current.end * 1000);
	end.setUTCFullYear(end.getUTCFullYear() - 1);

	return { start: start.valueOf() / 1000, end: end.valueOf() / 1000 };
}

export function getPerformanceReviewTemplate(group: Group): string {
	return getMetaValue(group, MetaField.PerformanceReviewTemplate) ?? '';
}

export const flattenGroups = (groups: Group[]) => {
	const flattenedGroups: Group[] = [];

	const flattenChildren = (group: Group) => {
		const index = flattenedGroups.findIndex((g) => g.id === group.id);

		if (group.children.length > 0) {
			flattenedGroups.splice(index + 1, 0, ...group.children);

			group.children.forEach((childGroup) => {
				flattenChildren(childGroup);
			});
		}
	};

	groups.forEach((g) => flattenChildren(g));

	return [...groups, ...flattenedGroups];
};

/**
 * Builds a hierarchical tree from a flat list of groups.
 * Automatically identifies top-level groups (those whose parentGroupId is not in the list)
 * and builds the tree structure from there.
 *
 * @param groups Flat array of groups to build the tree from
 * @param flatten If true, returns a flat array with depth properties; if false, returns nested structure
 *
 * @returns Group[] Array of top-level groups with their children nested
 */
export function buildGroupTree(groups: Group[], flatten?: boolean): Group[] {
	if (!groups || groups.length === 0) return [];

	// Filter out invalid groups to prevent infinite recursion
	const validGroups = groups.filter(
		(g) => g && typeof g.id === 'number' && g.id > 0
	);

	if (validGroups.length === 0) return [];

	const groupIds = new Set(validGroups.map((g) => g.id));

	const topLevelGroups = validGroups.filter(
		(g) => !g.parentGroupId || !groupIds.has(g.parentGroupId)
	);

	const findChildren = (group: Group) => {
		const children = validGroups
			.filter((g) => g.parentGroupId === group.id)
			.sort((a: Group, b: Group) => a.name.localeCompare(b.name));

		children.forEach((g) => {
			g.children = findChildren(g);
		});

		return children;
	};

	topLevelGroups.forEach((g) => {
		g.children = findChildren(g);
	});

	const addDepth = (groups: Group[], depth: number = 0) => {
		const unsortedGroups = groups.some((group) => group.order === -1);
		groups
			.sort(
				unsortedGroups
					? (a, b) => a.name.localeCompare(b.name)
					: (a: Group, b: Group) => a.order - b.order
			)
			.forEach((group: Group) => {
				group.depth = depth;

				if (group.children) {
					return addDepth(group.children, depth + 1);
				}
			});
	};

	addDepth(topLevelGroups, 0);

	if (flatten) {
		const flattenedGroups: Group[] = [];

		const flattenChildren = (group: Group) => {
			const index = flattenedGroups.findIndex((g) => g.id === group.id);

			if (group.children && group.children.length > 0) {
				flattenedGroups.splice(index + 1, 0, ...group.children);

				group.children.forEach((childGroup) => {
					flattenChildren(childGroup);
				});
			}
		};

		topLevelGroups.forEach((group) => {
			flattenedGroups.push(group);
			flattenChildren(group);
		});

		return flattenedGroups;
	}

	return topLevelGroups;
}

/**
 * Collects the ids of a group and all its descendants. Expects a group with
 * populated children, e.g. one returned by buildGroupTree.
 */
export function collectSubtreeIDs(group: Group): number[] {
	return [group.id, ...(group.children ?? []).flatMap(collectSubtreeIDs)];
}

export function filterGroupTree(nodes: Group[], search: string): Group[] {
	const needle = search.trim().toLowerCase();

	if (!needle) return nodes;

	const filter = (groups: Group[]): Group[] => {
		const result: Group[] = [];
		for (const node of groups) {
			const matchesSelf = node.name?.toLowerCase().includes(needle);
			const filteredChildren = node.children ? filter(node.children) : [];
			if (matchesSelf || filteredChildren.length > 0) {
				result.push({ ...node, children: filteredChildren });
			}
		}
		return result;
	};

	return filter(nodes);
}

function getTotalUnreadChats(organizations: OrganizationData[]) {
	if (!organizations) {
		return null;
	}

	return organizations.reduce((a, c) => a + c.unreadChatCount, 0);
}

function filterCurrentOrganization(
	organization: models.group.Group,
	organizations: OrganizationData[]
) {
	if (!organizations) {
		return null;
	}

	return organizations.filter((o) => o.id !== organization.id);
}

/**
 * Filters out provided org from a list of orgs and returns total unread chats
 * @param organization org you want to filter out
 * @param organizations list of orgs
 * @returns number of unread chats
 */
export function getUnreadChatsForOtherOrganizations(
	organization: models.group.Group,
	organizations: OrganizationData[]
) {
	return getTotalUnreadChats(
		filterCurrentOrganization(organization, organizations)
	);
}

/**
 *
 * @param groups that should be sorted by their order property
 */
export function sortByOrder(
	groups: models.group.Group[]
): models.group.Group[] {
	return groups.sort(
		(a: models.group.Group, b: models.group.Group) => a.order - b.order
	);
}

export function sortByName(groups: models.group.Group[]): models.group.Group[] {
	return groups.sort((a: models.group.Group, b: models.group.Group) =>
		a.name.localeCompare(b.name)
	);
}

export function getSplashScreenSettings(
	group?: Group
): Nullable<SplashScreenSettings> {
	if (!group) return null;

	const meta = getMetaValue(group, MetaField.SplashScreenSettings);

	if (meta) {
		return json.parse<SplashScreenSettings>(meta);
	}

	return null;
}
